import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * «Mover al carrito» on Hulk's wishlist page (/apps/favoritos), made to
 * behave like the theme's own «Agregar al carrito» (29 Sep): the line goes in,
 * the cart drawer repaints and opens with it.
 *
 * Hulk's button posts to the cart by itself and never tells the theme, so the
 * drawer and the cart count stay as they were until a reload. The click is
 * taken before Hulk sees it (capture on window) and the add is run the way
 * Horizon's writers run it (assets/mm-cart-gift.js, assets/product-form.js):
 * announce a CartLinesUpdateEvent, post with the sections to repaint, resolve
 * it with the new cart. cart-drawer.js opens itself on an `add`.
 *
 * The variant is the size picked in the card's select, or the one Hulk saved.
 * A refusal (a size with no stock) is shown in Hulk's own notice, which
 * mm-tokens.css section 45 styles, and the product stays in the wishlist.
 * Once added, it leaves the wishlist, as Hulk's own button did (its setting
 * «Eliminar de la lista de deseos después de agregar productos al carrito»):
 * through Hulk's own remove link and confirm button, so it works the same
 * for guests and customers, with its popup and notice kept out of sight
 * while it runs (`mm-wishlist-moving` on <html>, section 45).
 *
 * Depends on Hulk's `data-addmywishlistprotocart`, `data-hulkproductvariants`,
 * `data-varid` and `.msg-tooltip` (docs/overrides.md, third parties). If they
 * change, this does nothing and Hulk's own button works as before.
 */

/** @param {MouseEvent} event */
async function onClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-addmywishlistprotocart]') : null;
  if (!button) return;

  const card = button.closest('[data-varid]');
  const select = card?.querySelector('select[data-hulkproductvariants]');
  const variantId = Number(select?.value || card?.getAttribute('data-varid'));
  if (!variantId) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.hasAttribute('aria-busy')) return;
  button.setAttribute('aria-busy', 'true');

  const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .join(',');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections }),
    });
    const body = await response.json();
    const failed = !response.ok || Boolean(body.status);

    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: failed ? undefined : body.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'mm-wishlist-cart',
        didError: failed,
      },
    });

    if (failed) notify(body.description || body.message);
    else if (card) removeFromWishlist(card);
  } catch (error) {
    deferred.reject(error);
    notify();
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/**
 * @param {Element} card
 */
async function removeFromWishlist(card) {
  const remove = card.querySelector('[data-hulkdeletemywishlistproduct]');
  if (!(remove instanceof HTMLElement)) return;

  const root = document.documentElement;
  root.classList.add('mm-wishlist-moving');
  remove.click();

  const confirm = await waitFor('.wishlist-delete-popup.open [data-deletehulkwishlistitems]');
  if (confirm instanceof HTMLElement) confirm.click();

  setTimeout(() => {
    document.querySelector('.msg-tooltip')?.classList.remove('hulkActive');
    root.classList.remove('mm-wishlist-moving');
  }, 1500);
}

/**
 * @param {string} selector
 * @param {number} [timeout]
 * @returns {Promise<Element | null>}
 */
function waitFor(selector, timeout = 1500) {
  return new Promise((resolve) => {
    const start = performance.now();
    const check = () => {
      const element = document.querySelector(selector);
      if (element || performance.now() - start > timeout) resolve(element);
      else requestAnimationFrame(check);
    };
    check();
  });
}

/**
 * Hulk's notice, reused so the refusal reads like the rest of the page.
 * @param {string} [message]
 */
function notify(message) {
  const notice = document.querySelector('.msg-tooltip');
  const text = notice?.querySelector('.notification_details');
  if (!notice || !text) return;

  const fallback = document.querySelector('script[data-mm-wishlist-cart]')?.getAttribute('data-error') || '';
  text.textContent = message || fallback;
  notice.classList.add('hulkActive');
  setTimeout(() => notice.classList.remove('hulkActive'), 5000);
}

window.addEventListener('click', onClick, true);
